import type { ScrollBarOrientation } from "./types-scroll-bar";

/** Passo de uma seta (o da rolagem nativa por tecla). */
export const SCROLL_STEP_PX = 40;
/** Comprimento mínimo do polegar (Figma, nota do Scroll Bar mobile). */
export const MIN_THUMB_PX = 32;
/** Abaixo disto o conteúdo cabe: arredondamento do navegador não conta como estouro. */
const OVERFLOW_TOLERANCE_PX = 1;

export interface ScrollBarMeasure {
  overflowing: boolean;
  /** Tamanho visível da parte que rola no eixo (sem o `inset`). */
  viewport: number;
  /** Quanto o scroller rola no eixo. */
  scrollable: number;
  thumbSize: number;
  thumbOffset: number;
  /** Folga do polegar na trilha (trilha − polegar). */
  travel: number;
  /** Posição de rolagem de 0 a 100. */
  percent: number;
}

/**
 * `inset` é a parte do scroller que não rola no eixo (cabeçalho fixo, colunas congeladas): sai do visível e do total,
 * e o que rola fica igual.
 */
export function readAxis(scroller: HTMLElement, orientation: ScrollBarOrientation, inset = 0) {
  const vertical = orientation === "vertical";
  const client = vertical ? scroller.clientHeight : scroller.clientWidth;
  const fixed = Math.min(Math.max(inset, 0), client);
  const viewport = client - fixed;
  const total = (vertical ? scroller.scrollHeight : scroller.scrollWidth) - fixed;
  return {
    viewport,
    scrollable: total - viewport,
    total,
    scrolled: vertical ? scroller.scrollTop : scroller.scrollLeft,
  };
}

/** Rola até `position`, dentro do que o scroller rola (o jsdom não limita sozinho). */
export function scrollAxisTo(
  scroller: HTMLElement,
  orientation: ScrollBarOrientation,
  position: number,
) {
  const { scrollable } = readAxis(scroller, orientation);
  const next = Math.min(Math.max(position, 0), Math.max(scrollable, 0));
  if (orientation === "vertical") scroller.scrollTop = next;
  else scroller.scrollLeft = next;
}

/**
 * `rail` é o comprimento da trilha do polegar; sem layout (0) o estouro vale, mas o polegar fica sem medida. O polegar é
 * a fração visível da parte que rola (`inset` fora, ver `readAxis`).
 */
export function measureScrollBar(
  scroller: HTMLElement,
  rail: number,
  orientation: ScrollBarOrientation,
  inset = 0,
): ScrollBarMeasure {
  const { viewport, scrollable, total, scrolled } = readAxis(scroller, orientation, inset);
  const overflowing = scrollable > OVERFLOW_TOLERANCE_PX;
  const progress = overflowing ? Math.min(Math.max(scrolled / scrollable, 0), 1) : 0;
  const thumbSize =
    overflowing && rail > 0 ? Math.min(rail, Math.max(MIN_THUMB_PX, (rail * viewport) / total)) : 0;
  const travel = Math.max(rail - thumbSize, 0);
  return {
    overflowing,
    viewport,
    scrollable,
    thumbSize,
    thumbOffset: progress * travel,
    travel,
    percent: Math.round(progress * 100),
  };
}

/** Posição de rolagem que a tecla pede; `undefined` quando a tecla não é da barra. */
export function keyTarget(
  key: string,
  orientation: ScrollBarOrientation,
  scrolled: number,
  viewport: number,
  scrollable: number,
): number | undefined {
  const vertical = orientation === "vertical";
  switch (key) {
    case vertical ? "ArrowDown" : "ArrowRight":
      return scrolled + SCROLL_STEP_PX;
    case vertical ? "ArrowUp" : "ArrowLeft":
      return scrolled - SCROLL_STEP_PX;
    case "PageDown":
      return scrolled + viewport;
    case "PageUp":
      return scrolled - viewport;
    case "Home":
      return 0;
    case "End":
      return scrollable;
    default:
      return undefined;
  }
}
